import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "../lib/lang";

export default function HeroSlider({ slides }) {
  const { tri } = useLang();
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!slides?.length) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6000);
    return () => clearInterval(id);
  }, [slides]);

  if (!slides?.length) {
    return (
      <div className="h-[60vh] bg-slate-900 flex items-center justify-center text-slate-500" data-testid="hero-empty">
        No slides yet
      </div>
    );
  }

  const go = (n) => setIdx((idx + n + slides.length) % slides.length);

  return (
    <section className="relative h-[78vh] min-h-[520px] overflow-hidden bg-slate-900" data-testid="hero-slider">
      {slides.map((s, i) => (
        <div
          key={s.id}
          className={`kn-slide absolute inset-0 ${i === idx ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          aria-hidden={i !== idx}
        >
          <img src={s.image_url} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/55 to-slate-900/15" />
        </div>
      ))}

      <div className="relative z-10 max-w-7xl mx-auto px-6 h-full flex items-center">
        <div className="max-w-2xl text-white">
          <div className="inline-block bg-red-600 px-3 py-1 text-[10px] uppercase tracking-[0.3em] font-semibold mb-6" data-testid="hero-eyebrow">
            Official
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-none tracking-tight mb-6" data-testid={`hero-title-${idx}`}>
            {tri(slides[idx].title)}
          </h1>
          <p className="text-lg text-slate-200 max-w-xl mb-8" data-testid={`hero-subtitle-${idx}`}>
            {tri(slides[idx].subtitle)}
          </p>
          {slides[idx].cta_link && tri(slides[idx].cta_text) && (
            <Link
              to={slides[idx].cta_link}
              data-testid={`hero-cta-${idx}`}
              className="inline-block bg-white text-slate-900 px-6 py-3 text-sm uppercase tracking-[0.2em] font-semibold hover:bg-red-600 hover:text-white transition"
            >
              {tri(slides[idx].cta_text)}
            </Link>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="absolute bottom-8 right-8 z-10 flex gap-2">
        <button
          onClick={() => go(-1)}
          className="w-12 h-12 border border-white/40 text-white hover:bg-white hover:text-slate-900 flex items-center justify-center transition"
          aria-label="Previous"
          data-testid="hero-prev"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={() => go(1)}
          className="w-12 h-12 border border-white/40 text-white hover:bg-white hover:text-slate-900 flex items-center justify-center transition"
          aria-label="Next"
          data-testid="hero-next"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Dots */}
      <div className="absolute bottom-10 left-6 md:left-12 z-10 flex gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            className={`h-1 transition-all ${i === idx ? "w-12 bg-red-600" : "w-6 bg-white/40 hover:bg-white"}`}
            aria-label={`Slide ${i + 1}`}
            data-testid={`hero-dot-${i}`}
          />
        ))}
      </div>
    </section>
  );
}
